import React, { useState, useEffect } from 'react';
import { orderAPI, menuAPI } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { TrendingUp, DollarSign, ShoppingCart, Printer, Edit, Clock } from 'lucide-react';
import { LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';

export default function LiveDashboard() {
  const [orders, setOrders] = useState([]);
  const [menuItems, setMenuItems] = useState({});
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingOrder, setEditingOrder] = useState(null);
  const [editItems, setEditItems] = useState([]);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const ordersPerPage = 25;
  const [viewingOrder, setViewingOrder] = useState(null);
  
  // Date filter states
  const [dateFilter, setDateFilter] = useState('all'); // all, today, week, month, custom
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  // Helper function to get date range
  const getDateRange = () => {
    const now = new Date();
    let startDate, endDate;
    
    switch (dateFilter) {
      case 'today':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
        break;
      case 'week':
        const weekStart = new Date(now);
        weekStart.setDate(now.getDate() - now.getDay()); // Start of week (Sunday)
        startDate = new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate());
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
        break;
      case 'month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
        break;
      case 'custom':
        if (customStartDate && customEndDate) {
          startDate = new Date(customStartDate);
          endDate = new Date(customEndDate);
          endDate.setHours(23, 59, 59);
        }
        break;
      default:
        return null;
    }
    
    if (startDate && endDate) {
      return {
        start: startDate.toISOString().split('T')[0],
        end: endDate.toISOString().split('T')[0]
      };
    }
    return null;
  };

  // Fetch menu items
  useEffect(() => {
    const fetchMenu = async () => {
      try {
        const response = await menuAPI.getAll();
        if (response.data.success) {
          const itemMap = {};
          response.data.data.forEach(item => {
            itemMap[item._id] = item;
          });
          setMenuItems(itemMap);
        }
      } catch (err) {
        console.error('Failed to fetch menu:', err);
      }
    };
    fetchMenu();
  }, []);

  // Fetch orders
  const fetchOrders = async () => {
    try {
      setLoading(true);
      const dateRange = getDateRange();
      let response;
      
      if (filterStatus === 'all') {
        response = await orderAPI.getAll(dateRange?.start, dateRange?.end);
      } else {
        response = await orderAPI.getByStatus(filterStatus);
        // Filter by date on client side for status-filtered results
        if (dateRange && response.data.success) {
          const start = new Date(dateRange.start);
          const end = new Date(dateRange.end);
          response.data.data = response.data.data.filter(order => {
            const orderDate = new Date(order.created_at);
            return orderDate >= start && orderDate <= end;
          });
        }
      }
      
      if (response.data.success) {
        setOrders(response.data.data);
      }
    } catch (err) {
      setError('Failed to fetch orders');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Auto-refresh orders
  useEffect(() => {
    fetchOrders();
    setCurrentPage(1); // Reset to page 1 when filter changes
    if (!autoRefresh) return;
    
    const interval = setInterval(fetchOrders, 3000); // Update every 3 seconds
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, dateFilter, customStartDate, customEndDate, autoRefresh]);

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await orderAPI.updateStatus(orderId, newStatus);
      toast.success(`Order status updated to ${newStatus}`);
      fetchOrders();
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to update order status';
      toast.error(errorMessage);
      fetchOrders(); // Refresh to show current state
    }
  };

  const handlePrintBill = async (orderId) => {
    try {
      toast.success('Printing bill...');
      
      // Find the order
      const order = orders.find(o => o._id === orderId);
      if (!order) {
        toast.error('Order not found');
        return;
      }

      // Create print window with thermal printer optimized layout
      const printWindow = window.open('', '', 'width=300,height=600');
      const now = new Date();
      
      printWindow.document.write(`
        <html>
          <head>
            <title>Bill - Order #${order._id.substring(0, 8).toUpperCase()}</title>
            <style>
              @media print {
                @page {
                  size: 80mm auto;
                  margin: 0;
                }
                body {
                  margin: 0;
                  padding: 0;
                }
              }
              
              body {
                font-family: 'Courier New', monospace;
                margin: 0;
                padding: 8px;
                font-size: 12px;
                line-height: 1.4;
                width: 80mm;
                background: white;
              }
              
              .header {
                text-align: center;
                font-weight: bold;
                font-size: 16px;
                margin-bottom: 8px;
                border-bottom: 2px solid #000;
                padding-bottom: 8px;
              }
              
              .subheader {
                text-align: center;
                font-size: 11px;
                margin-bottom: 8px;
              }
              
              .divider {
                border-top: 1px dashed #000;
                margin: 8px 0;
              }
              
              .bold-divider {
                border-top: 2px solid #000;
                margin: 10px 0;
              }
              
              .section {
                margin: 8px 0;
                font-size: 11px;
              }
              
              .section-title {
                font-weight: bold;
                margin-bottom: 4px;
              }
              
              .items {
                margin: 8px 0;
              }
              
              .item {
                display: flex;
                justify-content: space-between;
                margin: 4px 0;
                font-size: 11px;
              }
              
              .item-name {
                flex: 1;
                font-weight: bold;
              }
              
              .item-details {
                display: flex;
                justify-content: space-between;
                margin-left: 8px;
                font-size: 10px;
                color: #333;
              }
              
              .item-qty {
                margin-right: 8px;
              }
              
              .total-section {
                margin-top: 10px;
                font-size: 12px;
              }
              
              .total-row {
                display: flex;
                justify-content: space-between;
                margin: 4px 0;
              }
              
              .total-row.grand {
                font-weight: bold;
                font-size: 14px;
                border-top: 2px solid #000;
                border-bottom: 2px solid #000;
                padding: 6px 0;
                margin-top: 8px;
              }
              
              .footer {
                text-align: center;
                font-size: 10px;
                margin-top: 12px;
                padding-top: 8px;
                border-top: 1px dashed #000;
              }
              
              .thank-you {
                text-align: center;
                font-size: 12px;
                font-weight: bold;
                margin: 8px 0;
              }
            </style>
          </head>
          <body>
            <div class="header">RESTAURANT BILL</div>
            <div class="subheader">Tax Invoice</div>
            
            <div class="section">
              <div><strong>Date:</strong> ${now.toLocaleDateString()}</div>
              <div><strong>Time:</strong> ${now.toLocaleTimeString()}</div>
              <div><strong>Order #:</strong> ${order._id.substring(0, 8).toUpperCase()}</div>
              ${order.table_number ? `<div><strong>Table:</strong> ${order.table_number}</div>` : ''}
              <div><strong>Type:</strong> ${order.order_type === 'dine_in' ? 'Dine In' : order.order_type === 'takeaway' ? 'Takeaway' : 'Delivery'}</div>
              ${order.customer_name ? `<div><strong>Customer:</strong> ${order.customer_name}</div>` : ''}
              ${order.customer_mobile ? `<div><strong>Mobile:</strong> ${order.customer_mobile}</div>` : ''}
              ${order.staff_name ? `<div><strong>Staff:</strong> ${order.staff_name}</div>` : ''}
            </div>
            
            <div class="divider"></div>
            
            <div class="items">
              <div class="item">
                <span style="flex: 1; font-weight: bold;">Item</span>
                <span style="width: 40px; text-align: center; font-weight: bold;">Qty</span>
                <span style="width: 60px; text-align: right; font-weight: bold;">Amount</span>
              </div>
              <div class="divider"></div>
              ${order.items.map(item => `
                <div style="margin-bottom: 8px;">
                  <div class="item">
                    <span class="item-name">${item.item_name}</span>
                    <span style="width: 40px; text-align: center;">${item.quantity}</span>
                    <span style="width: 60px; text-align: right;">₹${(item.price * item.quantity).toFixed(2)}</span>
                  </div>
                  <div style="font-size: 10px; color: #555; margin-left: 8px;">
                    @ ₹${item.price.toFixed(2)} each
                  </div>
                </div>
              `).join('')}
            </div>
            
            <div class="bold-divider"></div>
            
            <div class="total-section">
              <div class="total-row">
                <span>Subtotal:</span>
                <span>₹${order.total_amount.toFixed(2)}</span>
              </div>
              <div class="total-row grand">
                <span>TOTAL:</span>
                <span>₹${order.total_amount.toFixed(2)}</span>
              </div>
            </div>
            
            <div class="thank-you">THANK YOU!</div>
            <div class="footer">
              <p>Please visit again</p>
              <p style="margin-top: 4px;">Printed: ${now.toLocaleString()}</p>
            </div>
          </body>
        </html>
      `);
      
      printWindow.document.close();
      
      // Auto print after a short delay to ensure content is loaded
      setTimeout(() => {
        printWindow.print();
      }, 250);
      
    } catch (err) {
      console.error('Print error:', err);
      toast.error('Failed to print bill');
    }
  };

  const handleEditOrder = (order) => {
    setEditingOrder(order);
    setEditItems(JSON.parse(JSON.stringify(order.items)));
  };

  const handleUpdateQuantity = (index, quantity) => {
    const updated = [...editItems];
    updated[index].quantity = Math.max(1, quantity);
    setEditItems(updated);
  };

  const handleRemoveItem = (index) => {
    setEditItems(editItems.filter((_, i) => i !== index));
  };

  const calculateEditTotal = () => {
    return editItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  const handleSaveEdit = async () => {
    try {
      const updatedOrder = {
        ...editingOrder,
        items: editItems,
        total_amount: calculateEditTotal()
      };

      // Update order in database
      await orderAPI.updateOrder(editingOrder._id, updatedOrder);
      setEditingOrder(null);
      setEditItems([]);
      fetchOrders();
      alert('Order updated successfully');
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to update order';
      alert(errorMessage);
      fetchOrders(); // Refresh to show current state
      setEditingOrder(null);
    }
  };

  const statuses = ['preparing', 'ready'];

  // Calculate stats from orders
  const stats = {
    totalRevenue: orders.reduce((sum, order) => sum + order.total_amount, 0),
    ordersToday: orders.filter(o => {
      const orderDate = new Date(o.created_at);
      const today = new Date();
      return orderDate.toDateString() === today.toDateString();
    }).length,
    preparingCount: orders.filter(o => o.status === 'preparing').length,
    avgOrderValue: orders.length > 0 ? orders.reduce((sum, order) => sum + order.total_amount, 0) / orders.length : 0
  };

  // Prepare chart data
  const last7Days = Array.from({ length: 7 }, (_, i) => {
    const date = new Date();
    date.setDate(date.getDate() - (6 - i));
    return date.toISOString().split('T')[0];
  });

  const revenueData = last7Days.map(date => {
    const dayOrders = orders.filter(o => o.created_at.split('T')[0] === date);
    const revenue = dayOrders.reduce((sum, order) => sum + order.total_amount, 0);
    return {
      name: new Date(date).toLocaleDateString('en-US', { weekday: 'short' }),
      revenue: revenue
    };
  });

  // Category data from menu items
  const categoryData = Object.values(menuItems).reduce((acc, item) => {
    const category = item.category || 'Other';
    if (!acc[category]) {
      acc[category] = { name: category, value: 0, color: getCategoryColor(category) };
    }
    acc[category].value += 1;
    return acc;
  }, {});

  const categoryChartData = Object.values(categoryData);

  function getCategoryColor(category) {
    const colors = {
      'Main Course': '#f97316',
      'Appetizers': '#3b82f6',
      'Beverages': '#10b981',
      'Desserts': '#a855f7',
      'Starters': '#eab308',
      'Fast Food': '#ef4444'
    };
    return colors[category] || '#6b7280';
  }

  const getStatusBadgeColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      preparing: 'bg-blue-100 text-blue-700 border-blue-200',
      ready: 'bg-green-100 text-green-700 border-green-200',
      paid: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    };
    return colors[status] || 'bg-gray-100 text-gray-700 border-gray-200';
  };

  const getOrderTypeLabel = (type) => {
    const labels = {
      dine_in: 'Dine In',
      takeaway: 'Takeaway',
      delivery: 'Delivery'
    };
    return labels[type] || type;
  };

  // Pagination calculations
  const indexOfLastOrder = currentPage * ordersPerPage;
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
  const currentOrders = orders.slice(indexOfFirstOrder, indexOfLastOrder);
  const totalPages = Math.ceil(orders.length / ordersPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading && orders.length === 0) {
    return <div className="p-8 text-center text-gray-600">Loading live orders...</div>;
  }

  if (error) {
    return <div className="p-8 text-red-600 text-center">{error}</div>;
  }

  return (
    <div className="p-4 md:p-6 lg:p-8 h-full bg-gray-50">
      <div className="mb-4 md:mb-6">
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Live Dashboard</h2>
        <p className="text-gray-600">Real-time overview of your restaurant operations</p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-100 border-l-4 border-red-600 rounded-lg text-red-700 text-sm font-medium">
          {error}
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-gray-600">Total Revenue</p>
              <DollarSign className="w-5 h-5 text-green-600" />
            </div>
            <p className="text-2xl font-bold text-gray-900">₹{stats.totalRevenue.toFixed(2)}</p>
            <p className="text-sm text-green-600 mt-1">{orders.length} orders</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-gray-600">Orders Today</p>
              <ShoppingCart className="w-5 h-5 text-blue-600" />
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.ordersToday}</p>
            <p className="text-sm text-blue-600 mt-1">Active now</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-gray-600">Preparing</p>
              <Clock className="w-5 h-5 text-orange-600" />
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.preparingCount}</p>
            <p className="text-sm text-orange-600 mt-1">In kitchen</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-gray-600">Avg Order Value</p>
              <TrendingUp className="w-5 h-5 text-purple-600" />
            </div>
            <p className="text-2xl font-bold text-gray-900">₹{stats.avgOrderValue.toFixed(2)}</p>
            <p className="text-sm text-purple-600 mt-1">Per order</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 mb-6">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Weekly Revenue</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={revenueData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="revenue" stroke="#f97316" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Menu Categories</CardTitle>
          </CardHeader>
          <CardContent>
            {categoryChartData.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={categoryChartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {categoryChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="mt-4 space-y-2">
                  {categoryChartData.map((item) => (
                    <div key={item.name} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                        <span className="text-gray-700">{item.name}</span>
                      </div>
                      <span className="font-medium text-gray-900">{item.value}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-gray-500">
                No menu data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="space-y-4">
            {/* Status & Auto-refresh */}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex gap-2 flex-wrap">
                <Button
                  variant={filterStatus === 'all' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilterStatus('all')}
                >
                  All ({orders.length})
                </Button>
                {statuses.map((status) => {
                  const count = orders.filter(o => o.status === status).length;
                  return (
                    <Button
                      key={status}
                      variant={filterStatus === status ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setFilterStatus(status)}
                      className="capitalize"
                    >
                      {status} ({count})
                    </Button>
                  );
                })}
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                  className="w-4 h-4 accent-orange-600"
                />
                <span className="text-sm font-medium text-gray-700">Auto Refresh</span>
              </label>
            </div>

            {/* Date Filters */}
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Filter by Date</p>
              <div className="flex gap-2 flex-wrap items-center">
                {['all', 'today', 'week', 'month', 'custom'].map((filter) => (
                  <Button
                    key={filter}
                    variant={dateFilter === filter ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setDateFilter(filter)}
                    className="capitalize"
                  >
                    {filter === 'all' ? 'All Time' : filter}
                  </Button>
                ))}
                
                {dateFilter === 'custom' && (
                  <>
                    <Input
                      type="date"
                      value={customStartDate}
                      onChange={(e) => setCustomStartDate(e.target.value)}
                      className="w-40"
                    />
                    <span className="text-sm text-gray-500">to</span>
                    <Input
                      type="date"
                      value={customEndDate}
                      onChange={(e) => setCustomEndDate(e.target.value)}
                      className="w-40"
                    />
                  </>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Orders Table */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Orders</CardTitle>
          <p className="text-sm text-gray-600 mt-1">
            Page {currentPage} of {totalPages} • {orders.length} total orders
          </p>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Order ID</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Table</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Type</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Items</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Total</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Status</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Time</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentOrders.map((order) => (
                  <tr key={order._id} className="border-b hover:bg-gray-50 transition">
                    <td className="py-3 px-4 font-medium text-sm">
                      #{order._id.substring(0, 8).toUpperCase()}
                    </td>
                    <td className="py-3 px-4 text-sm">{order.table_number || 'N/A'}</td>
                    <td className="py-3 px-4 text-sm">{getOrderTypeLabel(order.order_type)}</td>
                    <td className="py-3 px-4 text-sm">{order.items.length}</td>
                    <td className="py-3 px-4 font-semibold text-sm">₹{order.total_amount.toFixed(2)}</td>
                    <td className="py-3 px-4">
                      <Badge className={getStatusBadgeColor(order.status)}>
                        {order.status}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 text-xs text-gray-600">
                      {new Date(order.created_at).toLocaleTimeString()}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setViewingOrder(order)}
                        >
                          View
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handlePrintBill(order._id)}
                        >
                          <Printer className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {orders.length === 0 && (
            <div className="text-center py-12">
              <ShoppingCart className="w-16 h-16 mx-auto text-gray-300 mb-3" />
              <p className="text-lg font-semibold text-gray-800">No orders found</p>
              <p className="text-gray-500 mt-1">Orders will appear here when placed</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex justify-center items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            ← Previous
          </Button>

          <div className="flex gap-1">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              
              return (
                <Button
                  key={pageNum}
                  variant={currentPage === pageNum ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handlePageChange(pageNum)}
                  className="min-w-[40px]"
                >
                  {pageNum}
                </Button>
              );
            })}
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next →
          </Button>
        </div>
      )}

      {/* View Order Dialog */}
      <Dialog open={!!viewingOrder} onOpenChange={() => setViewingOrder(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Order #{viewingOrder?._id.substring(0, 8).toUpperCase()}
            </DialogTitle>
            <p className="text-sm text-gray-600">
              {viewingOrder?.table_number && `Table ${viewingOrder.table_number} • `}
              {viewingOrder && getOrderTypeLabel(viewingOrder.order_type)}
            </p>
          </DialogHeader>

          {viewingOrder && (
            <div className="space-y-4">
              {/* Status Badge */}
              <div className="flex justify-center">
                <Badge className={`${getStatusBadgeColor(viewingOrder.status)} text-base px-6 py-2`}>
                  {viewingOrder.status.toUpperCase()}
                </Badge>
              </div>

              {/* Order Details */}
              <Card>
                <CardContent className="pt-6">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600">Order Time</p>
                      <p className="font-semibold">{new Date(viewingOrder.created_at).toLocaleString()}</p>
                    </div>
                    {viewingOrder.customer_name && (
                      <div>
                        <p className="text-gray-600">Customer</p>
                        <p className="font-semibold">{viewingOrder.customer_name}</p>
                      </div>
                    )}
                    {viewingOrder.customer_mobile && (
                      <div>
                        <p className="text-gray-600">Mobile</p>
                        <p className="font-semibold">{viewingOrder.customer_mobile}</p>
                      </div>
                    )}
                    {viewingOrder.staff_name && (
                      <div>
                        <p className="text-gray-600">Staff</p>
                        <p className="font-semibold">{viewingOrder.staff_name}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Order Items */}
              <div>
                <h3 className="font-semibold text-gray-800 mb-3">Order Items</h3>
                <div className="space-y-2">
                  {viewingOrder.items.map((item, idx) => (
                    <Card key={idx}>
                      <CardContent className="pt-4 pb-4">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <p className="font-semibold text-gray-800">{item.item_name}</p>
                            <p className="text-sm text-gray-600 mt-1">₹{item.price.toFixed(2)} × {item.quantity}</p>
                          </div>
                          <p className="font-bold text-lg">₹{(item.price * item.quantity).toFixed(2)}</p>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              {/* Total */}
              <Card className="bg-orange-50 border-orange-200">
                <CardContent className="pt-6">
                  <div className="flex justify-between items-center">
                    <p className="text-lg font-semibold text-gray-700">Total Amount</p>
                    <p className="text-3xl font-bold text-orange-600">₹{viewingOrder.total_amount.toFixed(2)}</p>
                  </div>
                </CardContent>
              </Card>

              {/* Actions */}
              <div className="flex gap-2">
                <Button
                  onClick={() => {
                    handlePrintBill(viewingOrder._id);
                    setViewingOrder(null);
                  }}
                  className="flex-1"
                  size="lg"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Print Bill
                </Button>
                <Button
                  onClick={() => {
                    handleEditOrder(viewingOrder);
                    setViewingOrder(null);
                  }}
                  variant="secondary"
                  className="flex-1"
                  size="lg"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Order
                </Button>
              </div>

              {/* Status Update */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Update Status</p>
                <Select
                  value={viewingOrder.status}
                  onValueChange={(value) => {
                    handleStatusChange(viewingOrder._id, value);
                    setViewingOrder(null);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {['preparing', 'ready', 'paid'].map((status) => (
                      <SelectItem key={status} value={status} className="capitalize">
                        {status}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Order Dialog - Keep old modal for now */}
      {editingOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
              {/* Modal Header */}
              <div className="bg-orange-600 p-5 sticky top-0">
                <h2 className="text-xl font-bold text-white">Edit Order #{editingOrder._id.substring(0, 8).toUpperCase()}</h2>
                <p className="text-orange-100 text-sm mt-1">Table {editingOrder.table_number} • {getOrderTypeLabel(editingOrder.order_type)}</p>
              </div>

              {/* Modal Content */}
              <div className="p-5">
                <div className="space-y-3 mb-6 max-h-64 overflow-y-auto">
                  {editItems.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-3 bg-gray-50 p-3 rounded-lg border border-gray-200">
                      <div className="flex-1">
                        <p className="font-semibold text-sm text-gray-800">{menuItems[item.item_id]?.name || 'Item'}</p>
                        <p className="text-xs text-gray-600">₹{item.price.toFixed(2)}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleUpdateQuantity(idx, item.quantity - 1)}
                          className="bg-red-600 hover:bg-red-700 text-white px-2.5 py-1.5 rounded-lg font-semibold transition text-sm"
                        >
                          −
                        </button>
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => handleUpdateQuantity(idx, parseInt(e.target.value))}
                          className="w-14 bg-white text-center text-gray-800 px-2 py-1.5 rounded-lg border border-gray-300 focus:border-orange-500 font-semibold text-sm"
                        />
                        <button
                          onClick={() => handleUpdateQuantity(idx, item.quantity + 1)}
                          className="bg-green-600 hover:bg-green-700 text-white px-2.5 py-1.5 rounded-lg font-semibold transition text-sm"
                        >
                          +
                        </button>
                      </div>
                      <div className="w-24 text-right">
                        <p className="font-semibold text-sm text-gray-800">₹{(item.price * item.quantity).toFixed(2)}</p>
                      </div>
                      <button
                        onClick={() => handleRemoveItem(idx)}
                        className="bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-lg font-medium transition text-sm"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>

                {/* Total */}
                <div className="border-t border-gray-200 pt-4 mb-6">
                  <div className="flex justify-between items-center">
                    <p className="text-sm font-semibold text-gray-700">Total Amount:</p>
                    <p className="text-2xl font-bold text-orange-600">
                      ₹{calculateEditTotal().toFixed(2)}
                    </p>
                  </div>
                </div>

                {/* Buttons */}
                <div className="flex gap-2">
                  <button
                    onClick={() => setEditingOrder(null)}
                    className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2.5 rounded-lg font-semibold transition text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveEdit}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2.5 rounded-lg font-semibold transition text-sm"
                  >
                    ✓ Save Changes
                  </button>
                </div>
              </div>
            </div>
          </div>
      )}
    </div>
  );
}
